import { action, computed, makeObservable, observable, values } from 'mobx';

import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { MemberDevice } from '@gem/call/CallStore/Member/MemberDevice';
import {
  CallMemberStatus,
  ICallMember,
  ParticipantsControlType,
} from '@gem/call/Model/callModel.types';

import { Call } from '../Call';
import { Member } from '../Member/Member';
import { HandsQueue } from './HandsQueue';

export class Members {
  @observable data = observable.map<string, Member>({}, { deep: false });
  handsQueue = new HandsQueue(this);

  call: Call;

  constructor(call: Call) {
    makeObservable(this);
    this.call = call;
  }

  @action
  setMembers(members: ICallMember[]): void {
    members.forEach((member) => {
      const existedMember = this.data.get(member.userId);
      if (existedMember) {
        existedMember.setDevice(member);
      } else {
        const newMember = new Member(this.call, member);

        this.data.set(member.userId, newMember);
      }
    });
  }

  @action
  deleteMember(userId: string): void {
    this.data.delete(userId);
  }

  // Members getters
  @computed
  get memberList(): Member[] {
    return values(this.data)
      .slice()
      .sort((member1, member2) => {
        return (
          Number(member2.isInitiator) * 10000 +
          Number(member2.isMe) * 1000 +
          Number(member1.isInitiator) * 10000 -
          Number(member1.isMe) * 1000 -
          (member1.displayName > member2.displayName ? 1 : 0) +
          (member1.displayName < member2.displayName ? -1 : 0)
        );
      });
  }

  @computed
  get memberDevicesList(): MemberDevice[] {
    const isGroupCall =
      this.call.uiFeatures.participantsControlType !== ParticipantsControlType.Disabled;

    return values(this.data)
      .reduce((acc, member) => {
        member.devicesList.forEach((memberDevice) => {
          acc.push(memberDevice);
        });
        return acc;
      }, [] as MemberDevice[])
      .sort((memberDevice1, memberDevice2) => {
        const priority1 = this.getMemberDevicePriority(memberDevice1, isGroupCall);
        const priority2 = this.getMemberDevicePriority(memberDevice2, isGroupCall);

        if (priority1 !== priority2) {
          return priority2 - priority1;
        }

        if (priority1 === 5) {
          const ts1 = memberDevice1.handRaisedAt ?? 0;
          const ts2 = memberDevice2.handRaisedAt ?? 0;
          if (ts1 !== ts2) {
            return ts1 - ts2;
          }
        }

        return (
          Number(memberDevice2.isMyDevice) * 10000 +
          Number(memberDevice2.member.isMe) * 1000 -
          Number(memberDevice1.isMyDevice) * 10000 -
          Number(memberDevice1.member.isMe) * 1000 +
          (memberDevice1.member.displayName > memberDevice2.member.displayName ? 1 : 0) +
          (memberDevice1.member.displayName < memberDevice2.member.displayName ? -1 : 0)
        );
      });
  }

  private getMemberDevicePriority(memberDevice: MemberDevice, isGroupCall: boolean): number {
    const member = memberDevice.member;

    // Участники с поднятой рукой
    if (memberDevice.isHandRaised && memberDevice.handRaisedAt != null) {
      return 5;
    }

    // Owner или Админы (если групповой звонок)
    if (member.isAdmin && isGroupCall) {
      return 4;
    }

    // Инициатор (если не Owner или Админ)
    if (!member.isAdmin && isGroupCall && member.isInitiator) {
      return 3;
    }

    // Остальные участники на звонке
    if (memberDevice.isOnCall) {
      return 2;
    }

    // Отсутствующие (Declined, Left)
    const state = memberDevice.state;
    if (state === CallMemberStatus.Declined || state === CallMemberStatus.Left) {
      return 1;
    }

    return 0;
  }

  @computed
  get memberDevicesOnCallList(): MemberDevice[] {
    return this.memberDevicesList.filter((member) => member.isOnCall);
  }

  getMemberById(userId: string): Member | null {
    return this.data.get(userId) || null;
  }

  getMemberDeviceByUid(uid: ProviderUid): MemberDevice | null {
    const memberDevices = values(this.data).reduce((acc, member) => {
      member.devices.forEach((memberDevice) => {
        acc.push(memberDevice);
      });
      return acc;
    }, [] as MemberDevice[]);

    return (
      memberDevices.find((memberDevice) => {
        return memberDevice.uid === uid;
      }) || null
    );
  }
}
